import {
  HOME_PAGE_LIMITS,
  HomePageSettings,
  HomePageTextField,
  HomeWords,
  OVERLAY_FALLBACK_COLOR,
  OVERLAY_FALLBACK_OPACITY,
  OVERLAY_OPACITY_MAX,
  OVERLAY_OPACITY_MIN,
  countChars,
  homeTextProblem,
  isHexColor,
  isValidOverlayOpacity,
  normalizeHomeText,
} from '../../utils/homePageSettings';

/** Spec 029: the page settings forms as pure functions (tested). */

export const WEB_FIELDS: HomePageTextField[] = ['webTitle', 'webSubtitle'];
export const APP_FIELDS: HomePageTextField[] = ['appTitle', 'appSubtitle'];

export type TextForm = Partial<Record<HomePageTextField, string>>;
export type HomePagePatch = Partial<Record<keyof HomePageSettings, string | number | null>>;

/** What the box starts with: the saved words, else the built-in default. */
export function initialTextForm(
  fields: HomePageTextField[],
  saved: HomePageSettings,
  defaults: HomeWords,
): TextForm {
  const form: TextForm = {};
  for (const f of fields) form[f] = saved[f] ?? defaults[f];
  return form;
}

export type TextFieldState = {
  count: number;
  limit: number;
  /** Shown under the box; null when the text can be saved. */
  error: string | null;
  /** The text equals the built-in default (it is stored as unset, D8). */
  isDefault: boolean;
};

export function textFieldState(
  field: HomePageTextField,
  value: string | undefined,
  defaults: HomeWords,
): TextFieldState {
  const text = normalizeHomeText(value);
  const limit = HOME_PAGE_LIMITS[field];
  const problem = homeTextProblem(field, value);
  return {
    count: countChars(text),
    limit,
    error:
      problem === 'too_long'
        ? `Use ${limit} characters or fewer.`
        : problem === 'control_characters'
          ? 'This text has characters that cannot be shown. Type it again.'
          : null,
    isDefault: text == null || text === defaults[field],
  };
}

/**
 * Only the fields that changed. Empty text or the default words are sent as
 * null, so the brand follows the built-in default (D8).
 */
export function buildTextPatch(
  fields: HomePageTextField[],
  form: TextForm,
  saved: HomePageSettings,
  defaults: HomeWords,
): HomePagePatch {
  const patch: HomePagePatch = {};
  for (const f of fields) {
    const text = normalizeHomeText(form[f]);
    const value = text == null || text === defaults[f] ? null : text;
    if (value !== (saved[f] ?? null)) patch[f] = value;
  }
  return patch;
}

export function canSaveText(
  fields: HomePageTextField[],
  form: TextForm,
  saved: HomePageSettings,
  defaults: HomeWords,
): boolean {
  if (fields.some(f => homeTextProblem(f, form[f]) != null)) return false;
  return Object.keys(buildTextPatch(fields, form, saved, defaults)).length > 0;
}

export type OverlayForm = {
  /** True = today's overlay (nothing stored). */
  standard: boolean;
  color: string;
  opacity: number;
};

export function initialOverlayForm(saved: HomePageSettings): OverlayForm {
  return {
    standard: saved.overlayColor == null && saved.overlayOpacity == null,
    color: saved.overlayColor ?? OVERLAY_FALLBACK_COLOR,
    opacity: saved.overlayOpacity ?? OVERLAY_FALLBACK_OPACITY,
  };
}

export function overlayErrors(form: OverlayForm): { color?: string; opacity?: string } {
  if (form.standard) return {};
  const errors: { color?: string; opacity?: string } = {};
  if (!isHexColor(form.color)) errors.color = 'Use a color like #14213D.';
  if (!isValidOverlayOpacity(form.opacity)) {
    errors.opacity = `Choose ${OVERLAY_OPACITY_MIN}% to ${OVERLAY_OPACITY_MAX}%.`;
  }
  return errors;
}

export function buildOverlayPatch(form: OverlayForm, saved: HomePageSettings): HomePagePatch {
  const color = form.standard ? null : form.color.trim().toUpperCase();
  const opacity = form.standard ? null : form.opacity;
  const patch: HomePagePatch = {};
  if (color !== (saved.overlayColor ?? null)) patch.overlayColor = color;
  if (opacity !== (saved.overlayOpacity ?? null)) patch.overlayOpacity = opacity;
  return patch;
}

export function canSaveOverlay(form: OverlayForm, saved: HomePageSettings): boolean {
  if (Object.keys(overlayErrors(form)).length) return false;
  return Object.keys(buildOverlayPatch(form, saved)).length > 0;
}

const SERVER_PROBLEM_TEXT: Record<string, (limit?: number) => string> = {
  too_long: limit => `Use ${limit} characters or fewer.`,
  control_characters: () => 'This text has characters that cannot be shown. Type it again.',
  bad_color: () => 'Use a color like #14213D.',
  bad_opacity: () => `Choose ${OVERLAY_OPACITY_MIN}% to ${OVERLAY_OPACITY_MAX}%.`,
};

/** `HOME_PAGE_SETTINGS_INVALID` details as a message per field; null for other errors. */
export function serverFieldErrors(error: unknown): Record<string, string> | null {
  const graphQLErrors = (error as any)?.graphQLErrors ?? [];
  const refusal = graphQLErrors.find(
    (e: any) => e?.extensions?.code === 'HOME_PAGE_SETTINGS_INVALID',
  );
  if (!refusal) return null;
  const out: Record<string, string> = {};
  for (const e of refusal.extensions?.details?.errors ?? []) {
    const text = SERVER_PROBLEM_TEXT[e?.problem];
    if (e?.field && text) out[e.field] = text(e.limit);
  }
  return out;
}
